import { analyzeCvBuilder, CvBuilderAnalyzeBody } from "src/services/cvBuilder.service";
import { CV_BUILDER_GUEST_ID_KEY } from "src/utils/toolProgressStorage";
import { Bullet, Resume } from "./types";

/** Batas gratis tidak dikirim BE di header, jadi FE yang tahu angkanya. */
export const FREE_AI_LIMIT = 5;
export const FREE_EXPORT_LIMIT = 1;

export type CvQuota = {
	guest_id: string | null;
	unlimited: boolean;
	ai_used: number;
	export_used: number;
};

export type QuotaKind = "ai" | "export";

const BULLET_LISTS = ["professional_experience", "education", "leadership_experience"] as const;
type BulletListKey = typeof BULLET_LISTS[number];

export type ChangeTarget =
	| { kind: "personal_description" }
	| { kind: "bullet"; list: BulletListKey; entryId: string; bulletId: string }
	| { kind: "other_detail"; entryId: string };

export type AiChange = {
	id: string;
	label: string;
	reason: string;
	target: ChangeTarget;
	before: string;
	after: string;
};

export type AiAnalysis = {
	summary: string;
	strengths: string[];
	gaps: string[];
	changes: AiChange[];
};

export type CvBuilderError = Error & { status: number | null; quota: CvQuota | null };

const AI_FAILED_MESSAGE = "Analisis AI sedang bermasalah. Silakan coba lagi.";

function cvError(message: string, status: number | null, quota: CvQuota | null): CvBuilderError {
	return Object.assign(new Error(message), { status, quota });
}

export const isQuotaExceeded = (error: unknown) => (error as CvBuilderError)?.status === 429;

/** 429 tidak memakai kuota, jadi aman diulang selama order baru menunggu webhook pembayaran. */
export async function retryWhileQuotaExceeded<R>(run: () => Promise<R>, retries: number, delayMs: number): Promise<R> {
	for (let attempt = 0; ; attempt += 1) {
		try {
			return await run();
		} catch (error) {
			if (!isQuotaExceeded(error) || attempt >= retries) throw error;
			await new Promise((resolve) => window.setTimeout(resolve, delayMs));
		}
	}
}

const toCount = (value: unknown) => {
	const n = Number(value);
	return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

export function parseQuota(raw: unknown): CvQuota | null {
	if (typeof raw !== "string" || !raw.trim()) return null;
	try {
		const value = JSON.parse(raw);
		if (!value || typeof value !== "object") return null;
		return {
			guest_id: typeof value.guest_id === "string" && value.guest_id ? value.guest_id : null,
			unlimited: value.unlimited === true,
			ai_used: toCount(value.ai_used),
			export_used: toCount(value.export_used),
		};
	} catch {
		return null;
	}
}

/** `null` berarti tanpa batas. */
export function remainingQuota(quota: CvQuota | null, kind: QuotaKind): number | null {
	if (quota?.unlimited) return null;
	if (kind === "ai") return Math.max(0, FREE_AI_LIMIT - (quota?.ai_used ?? 0));
	return Math.max(0, FREE_EXPORT_LIMIT - (quota?.export_used ?? 0));
}

function readGuestId() {
	try {
		return localStorage.getItem(CV_BUILDER_GUEST_ID_KEY);
	} catch {
		return null;
	}
}

function rememberGuest(quota: CvQuota | null) {
	if (!quota?.guest_id) return;
	try {
		localStorage.setItem(CV_BUILDER_GUEST_ID_KEY, quota.guest_id);
	} catch {
		// Mode privat atau storage penuh: kuota tetap jalan, hanya guest_id yang tidak bertahan.
	}
}

function errorMessage(status: number | null, serverMessage: unknown) {
	const message = typeof serverMessage === "string" && serverMessage.trim() ? serverMessage : "";
	if (status === null) return "Tidak dapat terhubung ke server. Periksa koneksi internet lalu coba lagi.";
	if (status === 429) return message || "Kuota gratis sudah habis.";
	if (status === 502) return message || AI_FAILED_MESSAGE;
	return message || `Terjadi kesalahan (HTTP ${status}). Silakan coba lagi.`;
}

async function callAnalyze(body: CvBuilderAnalyzeBody) {
	// User login dihitung dari JWT; guest_id hanya dikirim untuk tamu.
	const guestId = localStorage.getItem("TOKEN") ? null : readGuestId();
	try {
		const response = await analyzeCvBuilder(body, guestId);
		const quota = parseQuota(response?.headers?.["x-cv-quota"]);
		rememberGuest(quota);
		return { data: response?.data, quota };
	} catch (error) {
		const response = (error as { response?: { status?: number; headers?: Record<string, unknown>; data?: { message?: unknown } } })
			?.response;
		const quota = parseQuota(response?.headers?.["x-cv-quota"]);
		rememberGuest(quota);
		const status = response?.status ?? null;
		throw cvError(errorMessage(status, response?.data?.message), status, quota);
	}
}

const asText = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const asTextList = (value: unknown) => (Array.isArray(value) ? value.map(asText).filter(Boolean) : []);

function normalizeTarget(raw: unknown): ChangeTarget | null {
	if (!raw || typeof raw !== "object") return null;
	const t = raw as Record<string, unknown>;
	if (t.kind === "personal_description") return { kind: "personal_description" };
	if (t.kind === "bullet") {
		const list = BULLET_LISTS.find((key) => key === t.list);
		const entryId = asText(t.entryId);
		const bulletId = asText(t.bulletId);
		return list && entryId && bulletId ? { kind: "bullet", list, entryId, bulletId } : null;
	}
	if (t.kind === "other_detail") {
		const entryId = asText(t.entryId);
		return entryId ? { kind: "other_detail", entryId } : null;
	}
	return null;
}

/** Saran yang menunjuk entri/poin yang sudah dihapus user tidak ditampilkan. */
function targetExists(resume: Resume, target: ChangeTarget) {
	if (target.kind === "personal_description") return true;
	if (target.kind === "other_detail") return (resume?.others || []).some((item) => item?.id === target.entryId);
	const entries: { id: string; elaboration: Bullet[] }[] = resume?.[target.list] || [];
	const entry = entries.find((item) => item?.id === target.entryId);
	return (entry?.elaboration || []).some((bullet) => bullet?.id === target.bulletId);
}

/** Isi `choices[0].message.content` dari body chat completion BE. */
export function parseAnalysisContent(content: unknown, resume: Resume): AiAnalysis | null {
	if (typeof content !== "string") return null;
	let raw = content.trim();
	const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
	if (fenced) raw = fenced[1].trim();
	const start = raw.indexOf("{");
	const end = raw.lastIndexOf("}");
	if (start === -1 || end <= start) return null;

	let parsed: Record<string, unknown>;
	try {
		parsed = JSON.parse(raw.slice(start, end + 1));
	} catch {
		return null;
	}
	if (!parsed || typeof parsed !== "object") return null;

	const changes: AiChange[] = [];
	(Array.isArray(parsed.changes) ? parsed.changes : []).forEach((item: Record<string, unknown>, i: number) => {
		const target = normalizeTarget(item?.target);
		const after = asText(item?.after);
		if (!target || !after || !targetExists(resume, target)) return;
		changes.push({
			id: asText(item?.id) || `chg-${i + 1}`,
			label: asText(item?.label) || `Perubahan ${i + 1}`,
			reason: asText(item?.reason),
			target,
			before: asText(item?.before),
			after,
		});
	});

	return {
		summary: asText(parsed.summary),
		strengths: asTextList(parsed.strengths),
		gaps: asTextList(parsed.gaps),
		changes,
	};
}

/** Foto (base64) dan pengaturan tampilan tidak relevan untuk analisis dan memperbesar request. */
function resumeForAi(resume: Resume) {
	const { profile_image, ...personal } = resume?.personal_details || ({} as Resume["personal_details"]);
	return {
		personal_details: personal,
		professional_experience: resume?.professional_experience || [],
		education: resume?.education || [],
		leadership_experience: resume?.leadership_experience || [],
		others: resume?.others || [],
	};
}

export async function analyzeResume(resume: Resume): Promise<{ analysis: AiAnalysis; quota: CvQuota | null }> {
	const { data, quota } = await callAnalyze({ resume: resumeForAi(resume) });
	const analysis = parseAnalysisContent(data?.choices?.[0]?.message?.content, resume);
	if (!analysis) throw cvError(AI_FAILED_MESSAGE, 502, quota);
	return { analysis, quota };
}

/** Wajib dipanggil sebelum membuat file. 429 berarti file tidak boleh dibuat. */
export async function requestExport(): Promise<CvQuota | null> {
	const { quota } = await callAnalyze({ action: "export" });
	return quota;
}

export async function requestQuotaStatus(): Promise<CvQuota | null> {
	const { quota } = await callAnalyze({ action: "status" });
	return quota;
}

export function applyResumeChanges(resume: Resume, changes: AiChange[]): Resume {
	if (!resume || !Array.isArray(changes) || changes.length === 0) return resume;
	return changes.reduce<Resume>((next, change) => {
		const { target, after } = change;
		if (target.kind === "personal_description") {
			return { ...next, personal_details: { ...next.personal_details, description: after } };
		}
		if (target.kind === "other_detail") {
			return {
				...next,
				others: (next.others || []).map((item) =>
					item.id === target.entryId ? { ...item, elaboration: { ...(item.elaboration || {}), text: after } } : item
				),
			};
		}
		const list: { id: string; elaboration: Bullet[] }[] = next[target.list] || [];
		return {
			...next,
			[target.list]: list.map((entry) =>
				entry.id === target.entryId
					? {
							...entry,
							elaboration: (entry.elaboration || []).map((bullet) =>
								bullet.id === target.bulletId ? { ...bullet, text: after } : bullet
							),
					  }
					: entry
			),
		};
	}, resume);
}
